/**
 * Application shell.
 *
 * Directive §6 responsive contract:
 *   mobile  < 1024px: five role-aware destinations in fixed bottom navigation
 *   desktop >= 1024px — sticky top navigation with Search, Save state, Avatar
 *   bottom navigation is hidden during the New Trip wizard and Field Mode
 *   touch targets >= 44px, main content max 1180px
 *
 * Directive §9: the AAG mark keeps its approved placement.
 * Directive §10: screen fade ~0.25s, auto-save shows "Saving..." then "Saved".
 */

import { type ReactNode } from 'react';
import {
  HomeIcon, UsersIcon, CalendarDaysIcon, EllipsisHorizontalIcon,
  CalendarIcon, TruckIcon, MagnifyingGlassIcon, PhoneIcon,
} from '@heroicons/react/24/outline'; // §9 §62: bundled, never a CDN
import { AagMark } from './AagMark.js';
import type { PresenceState } from '../hooks/usePresence.js';
import { isNativeMobile } from '../mobile/platform.js';

export type ScreenId =
  | 'home' | 'people' | 'person' | 'field-work' | 'visits' | 'map' | 'more'
  | 'calendar' | 'teams' | 'trip-wizard' | 'team-route' | 'field-mode'
  | 'import' | 'locations-review' | 'profile' | 'help' | 'admin' | 'call-center' | 'forms'
  | 'team-assignments' | 'av-review';

/** Screens that take over the viewport entirely. §6 */
const IMMERSIVE: ReadonlySet<ScreenId> = new Set<ScreenId>(['trip-wizard', 'field-mode']);

export type SaveState = 'idle' | 'saving' | 'saved';

interface NavItem {
  id: ScreenId;
  label: string;
  Icon: typeof HomeIcon;
}

const MANAGER_MOBILE_NAV: NavItem[] = [
  { id: 'home', label: 'Home', Icon: HomeIcon },
  { id: 'people', label: 'Cases', Icon: UsersIcon },
  { id: 'field-work', label: 'Field Work', Icon: TruckIcon },
  { id: 'team-assignments', label: 'Planning', Icon: CalendarDaysIcon },
  { id: 'more', label: 'Menu', Icon: EllipsisHorizontalIcon },
];

const TEAM_MOBILE_NAV: NavItem[] = [
  { id: 'home', label: 'Home', Icon: HomeIcon },
  { id: 'people', label: 'Cases', Icon: UsersIcon },
  { id: 'field-work', label: 'My Team', Icon: TruckIcon },
  { id: 'call-center', label: 'Phone', Icon: PhoneIcon },
  { id: 'more', label: 'Menu', Icon: EllipsisHorizontalIcon },
];

const NATIVE_MOBILE_NAV: NavItem[] = [
  { id: 'call-center', label: 'Secure Phone', Icon: PhoneIcon },
];

const MANAGER_DESKTOP_NAV: NavItem[] = [
  { id: 'home', label: 'Home', Icon: HomeIcon },
  { id: 'people', label: 'People & Cases', Icon: UsersIcon },
  { id: 'field-work', label: 'Field Work', Icon: TruckIcon },
  { id: 'team-assignments', label: 'Planning', Icon: CalendarIcon },
  { id: 'call-center', label: 'Call Center', Icon: PhoneIcon },
  { id: 'more', label: 'Menu', Icon: EllipsisHorizontalIcon },
];

const TEAM_DESKTOP_NAV: NavItem[] = [
  { id: 'home', label: 'Home', Icon: HomeIcon },
  { id: 'people', label: 'People & Cases', Icon: UsersIcon },
  { id: 'field-work', label: 'My Team', Icon: TruckIcon },
  { id: 'call-center', label: 'Call Center', Icon: PhoneIcon },
  { id: 'more', label: 'Menu', Icon: EllipsisHorizontalIcon },
];

function selectedNav(screen: ScreenId): ScreenId {
  if (screen === 'person' || screen === 'import') return 'people';
  if (['visits', 'teams', 'team-route', 'field-mode', 'map', 'calendar', 'forms']
    .includes(screen)) return 'field-work';
  if (screen === 'trip-wizard' || screen === 'locations-review') return 'team-assignments';
  if (['profile', 'help', 'admin', 'av-review'].includes(screen)) return 'more';
  return screen;
}

export interface AppShellProps {
  screen: ScreenId;
  onNavigate: (id: ScreenId) => void;
  saveState: SaveState;
  userInitials: string;
  canManagePlanning?: boolean;
  presence: PresenceState;
  onAccountClick?: () => void;
  onSearch?: (q: string) => void;
  children: ReactNode;
}

export function AppShell({
  screen, onNavigate, saveState, userInitials, presence, onAccountClick, onSearch,
  canManagePlanning = false, children,
}: AppShellProps) {
  const immersive = IMMERSIVE.has(screen);
  const nativeMobile = isNativeMobile();

  return (
    <div className={`min-h-screen bg-bg flex flex-col ${screen === 'people' ? 'people-context' : ''}`}>
      <TopBar
        screen={screen}
        onNavigate={onNavigate}
        saveState={saveState}
        userInitials={userInitials}
        presence={presence}
        onAccountClick={onAccountClick}
        onSearch={onSearch}
        immersive={immersive}
        nativeMobile={nativeMobile}
        canManagePlanning={canManagePlanning}
      />

      <main
        // Bottom padding clears the fixed nav plus the home-indicator inset. §6
        className={[
          'w-full flex-1 animate-screen-in',
          screen === 'map' ? 'max-w-none p-0 min-h-0' : 'max-w-content px-4 desktop:px-6',
          screen === 'map' || immersive
            ? 'pb-0'
            : 'pb-[calc(80px+env(safe-area-inset-bottom))] desktop:pb-8',
        ].join(' ')}
      >
        {children}
      </main>

      {!immersive && !nativeMobile && (
        <BottomNav screen={screen} onNavigate={onNavigate} nativeMobile={nativeMobile}
          canManagePlanning={canManagePlanning} />
      )}
    </div>
  );
}

function TopBar({
  screen, onNavigate, saveState, userInitials, presence, onAccountClick, onSearch, immersive,
  nativeMobile,
  canManagePlanning,
}: Omit<AppShellProps, 'children'> & { immersive: boolean; nativeMobile: boolean }) {
  const navigation = nativeMobile ? NATIVE_MOBILE_NAV
    : canManagePlanning ? MANAGER_DESKTOP_NAV : TEAM_DESKTOP_NAV;
  return (
    <header className="sticky top-0 z-30 bg-bg shadow-[0_1px_0_#eae7e7]">
      <div className="px-4 desktop:px-6 min-h-[64px] flex items-center gap-4 desktop:gap-3">
        <div className="flex items-center gap-2 desktop:gap-3 shrink-0">
          <AagMark className={screen === 'people' ? 'h-3 w-auto desktop:h-3' : 'h-5 w-auto'} />
          <span className="w-px h-4 desktop:h-[22px] bg-neutral-400" aria-hidden />
          <span className="flex flex-col leading-none">
            <span className="font-heading font-semibold text-[17px] desktop:text-h4">Veritas</span>
            <span className="text-[10px] desktop:text-[11px] tracking-[.08em] uppercase text-neutral-700 mt-[2px]">
              {nativeMobile ? 'Secure Communications' : 'Route Mapping'}
            </span>
          </span>
        </div>

        {/* Desktop-only primary navigation. §6 */}
        {!immersive && (
          <nav className="hidden desktop:flex items-center gap-3" aria-label="Primary">
            {navigation.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => onNavigate(id)}
                aria-current={selectedNav(screen) === id ? 'page' : undefined}
                className={[
                  'min-h-touch px-0 font-heading text-[15px] transition-colors whitespace-nowrap',
                  selectedNav(screen) === id
                    ? 'text-accent-700'
                    : 'text-text hover:text-accent-700',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-3">
          {onSearch && (
            <label className="hidden desktop:flex items-center relative">
              <MagnifyingGlassIcon
                className="w-5 h-5 absolute left-2 text-neutral-600" aria-hidden
              />
              <span className="sr-only">Search</span>
              <input
                type="search"
                placeholder="Search people, cases, addresses"
                onChange={(e) => onSearch(e.target.value)}
                className="input pl-8 w-[clamp(160px,18vw,280px)] desktop:!min-h-[34px] desktop:py-1"
              />
            </label>
          )}

          <SaveIndicator state={saveState} />

          <AccountAvatar initials={userInitials} presence={presence} onClick={onAccountClick} />
        </div>
      </div>
    </header>
  );
}

const PRESENCE_LABEL: Record<PresenceState, string> = {
  active: 'Active',
  idle: 'Idle',
  'logged-out': 'Logged out',
};

const PRESENCE_DOT: Record<PresenceState, string> = {
  active: 'bg-presence-active',
  idle: 'bg-presence-idle',
  'logged-out': 'bg-presence-offline',
};

export function AccountAvatar({
  initials, presence, onClick,
}: {
  initials: string;
  presence: PresenceState;
  onClick?: () => void;
}) {
  const contents = <>
    <span aria-hidden>{initials || '?'}</span>
    <span
      className={`absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-2 border-bg ${PRESENCE_DOT[presence]}`}
      aria-hidden
    />
    <span className="sr-only">{PRESENCE_LABEL[presence]}</span>
  </>;
  const className = 'relative flex h-11 w-11 desktop:h-9 desktop:w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 font-heading text-xs font-semibold text-accent-800';

  if (!onClick) {
    return <span className={className} role="status" aria-label={`Account status: ${PRESENCE_LABEL[presence]}`}>{contents}</span>;
  }

  return <button
    type="button"
    className={`${className} border-0 hover:bg-accent-200 focus-visible:ring-2 focus-visible:ring-accent-300`}
    aria-label={`Open profile. Account status: ${PRESENCE_LABEL[presence]}`}
    title={`Open profile, ${PRESENCE_LABEL[presence].toLowerCase()}`}
    onClick={onClick}
  >{contents}</button>;
}

/**
 * Auto-save indicator. §10 preserves the "Saving..." / "Saved" copy.
 * The cosmetic delay lives in the caller; server durability is never tied to it.
 */
export function SaveIndicator({ state }: { state: SaveState }) {
  return (
    <span
      role="status"
      aria-live="polite"
      className="text-sm text-neutral-700 min-w-[56px] text-right"
    >
      {state === 'saving' ? 'Saving...' : state === 'saved' ? 'Saved' : ''}
    </span>
  );
}

function BottomNav({
  screen, onNavigate, nativeMobile, canManagePlanning,
}: { screen: ScreenId; onNavigate: (id: ScreenId) => void; nativeMobile: boolean;
  canManagePlanning: boolean }) {
  const navigation = nativeMobile ? NATIVE_MOBILE_NAV
    : canManagePlanning ? MANAGER_MOBILE_NAV : TEAM_MOBILE_NAV;
  return (
    <nav
      aria-label="Primary"
      className="desktop:hidden fixed bottom-0 inset-x-0 z-30
                 bg-bg shadow-[0_-1px_0_#eae7e7]
                 pb-[max(10px,env(safe-area-inset-bottom))]"
    >
      <ul className={nativeMobile ? 'flex justify-center' : 'flex'}>
        {navigation.map(({ id, label, Icon }) => {
          const active = selectedNav(screen) === id;
          return (
            <li key={id} className={nativeMobile ? 'w-full max-w-[280px]' : 'flex-1'}>
              <button
                onClick={() => onNavigate(id)}
                aria-current={active ? 'page' : undefined}
                // §6: every touch target is at least 44px.
                className={[
                'w-full min-h-[52px] py-1 flex flex-col items-center gap-[2px]',
                  active ? 'text-accent-700' : 'text-text',
                ].join(' ')}
              >
                <Icon className="w-5 h-5" aria-hidden />
                <span className="text-[12px] leading-none">{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
